/**
 * Геометрия комнат-прямоугольников, которые чертят инструментом стены: углы повёрнутого
 * прямоугольника и разбор введённых с клавиатуры размеров. Прямоугольник по осям
 * раскладывает `roomLayout`, как «Создать комнату».
 *
 * Углы идут по контуру друг за другом: стены строятся от угла к следующему, последняя
 * замыкается на первый. Начерченный контур — внутренняя грань комнаты: отдельно стоящую
 * комнату стены обступают снаружи (`inflateClosedContour`), и её размеры — в чистоте.
 */
import { add, dist, dot, leftNormal, mul, norm, sub, type Pt } from "@/shared/lib/geometry";

/**
 * Повёрнутый прямоугольник: первая стена от `a` до `b`, ширина откладывается поперёк неё.
 * Знак ширины — в какую сторону от первой стены.
 */
export const rotatedCorners = (a: Pt, b: Pt, width: number): Pt[] => {
	const across = mul(leftNormal(norm(sub(b, a))), width);
	return [a, b, add(b, across), add(a, across)];
};

/** На сколько точка `p` отстоит от линии `a`–`b` — со знаком, как у `rotatedCorners`. */
export const widthFrom = (a: Pt, b: Pt, p: Pt): number =>
	dot(sub(p, a), leftNormal(norm(sub(b, a))));

/** Длины двух соседних сторон прямоугольника. */
export const sidesOf = (corners: Pt[]): [number, number] => [
	dist(corners[0], corners[1]),
	dist(corners[1], corners[2]),
];

/**
 * Размеры, введённые с клавиатуры: «3000 2000», «3000x2000», «3000*2000» — ширина и длина,
 * одно число — квадрат. null — ввели не то.
 */
export const parseSize = (text: string): [number, number] | null => {
	const parts = text
		.trim()
		.split(/[\sx×*хХ]+/u)
		.filter(Boolean)
		.map((part) => Number(part.replace(",", ".")));
	if (parts.length === 0 || parts.length > 2 || parts.some((n) => !Number.isFinite(n))) {
		return null;
	}
	return [parts[0], parts[1] ?? parts[0]];
};
